<template>
  <section id="features" class="features">
    <div class="container">
      <SectionTitle :title="t('features.title')" :subtitle="t('features.subtitle')" />
      <div class="features-grid">
        <div
          v-for="(item, index) in features"
          :key="item.title"
          class="feature-card"
          :style="{ transitionDelay: `${index * 80}ms` }"
          :class="{ visible: visible }"
          ref="cardsRef"
        >
          <div class="icon-box" :style="{ background: iconBg(index) }">
            <IconSvg :name="item.icon" class="feature-icon" />
          </div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import SectionTitle from './SectionTitle.vue'
import IconSvg from './IconSvg.vue'

const { t, tm } = useI18n()
const features = computed(() => tm('features.items'))

const iconBgs = [
  'rgba(59, 130, 246, 0.1)',
  'rgba(239, 68, 68, 0.1)',
  'rgba(16, 185, 129, 0.1)',
  'rgba(245, 158, 11, 0.1)',
  'rgba(139, 92, 246, 0.1)',
  'rgba(236, 72, 153, 0.1)'
]
const iconBg = (i) => iconBgs[i]

const cardsRef = ref([])
const visible = ref(false)

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visible.value = true
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.15 })
  cardsRef.value.forEach((card) => {
    if (card) observer.observe(card)
  })
})
</script>

<style scoped>
.features {
  padding: 6rem 0;
  background: var(--bg-light);
}
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.feature-card {
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
  transition: transform 0.3s, box-shadow 0.3s, opacity 0.6s ease, transform 0.6s ease;
  opacity: 0;
  transform: translateY(30px);
}
.feature-card.visible {
  opacity: 1;
  transform: translateY(0);
}
.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.08);
}
.icon-box {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  color: var(--text-dark);
}
.feature-icon {
  width: 32px;
  height: 32px;
}
.feature-card h3 {
  font-size: 1.15rem;
  margin: 0 0 0.75rem;
  color: var(--text-dark);
}
.feature-card p {
  color: #6b5f5a;
  line-height: 1.7;
  margin: 0;
  font-size: 0.95rem;
}
@media (max-width: 900px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .features-grid {
    grid-template-columns: 1fr;
  }
}
</style>
